import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-loanRefiCalculator',
  templateUrl: './loan-refi-calculator.component.html',
  styleUrls: ['./loan-refi-calculator.component.scss']
})

export class LoanRefiCaclulatorComponent implements OnInit {
  termOptions = [
    { value: 30, label: '30 years' },
    { value: 15, label: '15 years' }
  ];

  // Old loan state
  currentLoanAmount: number = 350000;
  currentInterestRate: number = 7.50;
  currentTerm: number = 30;
  originationYear: number = 2020;

  // New loan state
  newBaseLoanAmount: number = 300000;
  newLoanAmount: number = 300000;
  newInterestRate: number = 6.3;
  newTerm: number = 30;
  refinanceFees: number = 3500;

  // Results state
  monthlySavings: number = 0;
  newPayment: number = 0;
  breakEven: number | string = 0;
  lifetimeSavings: number = 0;

  // Cash Out
  cashOut: number = 0;
  rollFees: boolean = false;

  // Savings
  fiveYearSavings: number = 0;
  tenYearSavings: number = 0;
  fifteenYearSavings: number = 0;
  twentyYearSavings: number = 0;

  // Intro Text
  refinanceCalcIntro: string = 'Explore whether refinancing your mortgage is the right move.';

  constructor() { }

  ngOnInit(): void {
    this.doCalculation();
  }

  doCalculation() {
    this.newLoanAmount = this.newBaseLoanAmount + this.cashOut
    if (this.rollFees) {
      this.newLoanAmount = this.newBaseLoanAmount + this.refinanceFees;
    }

    // Current Payment Calculations
    const currentLoanAmount = this.currentLoanAmount;
    const currentInterestRate = (this.currentInterestRate / 100) / 12; // interest rate per month
    const currentMonths = this.currentTerm * 12; // number of months
    const currentDenominator = (Math.pow((1 + currentInterestRate), currentMonths) - 1) / (currentInterestRate * (Math.pow((1 + currentInterestRate), currentMonths)));

    const currentPayment = Math.round(currentLoanAmount / currentDenominator);

    // New Payment Calculations
    const newInterestRate = (this.newInterestRate / 100) / 12; // new interest rate per month
    const newMonths = this.newTerm * 12; // number of months
    const newDenominator = (Math.pow((1 + newInterestRate), newMonths) - 1) / (newInterestRate * (Math.pow((1 + newInterestRate), newMonths)));

    this.newPayment = Math.round(this.newLoanAmount / newDenominator);

    // Results Calculations
    this.monthlySavings = currentPayment - this.newPayment;

    const currentYear = new Date().getFullYear();
    const yearCurrentLoanEnds = this.originationYear + this.currentTerm;
    const monthsLeftOnCurrentLoan = (yearCurrentLoanEnds - currentYear) * 12;

    const generatedLifetimeSavings = this.GenerateLifetimeSavings(newMonths, 252, monthsLeftOnCurrentLoan, currentPayment);
    this.lifetimeSavings = generatedLifetimeSavings[generatedLifetimeSavings.length - 2].savings;

    const breakEvenPoint = generatedLifetimeSavings.find(el => el.savings > this.monthlySavings);

    if (breakEvenPoint && !this.rollFees) {
      this.breakEven = breakEvenPoint.month;
    } else if (!breakEvenPoint) {
      this.breakEven = 'N/A';
      this.fiveYearSavings = null;
      this.tenYearSavings = null;
      this.fifteenYearSavings = null;
      this.twentyYearSavings = null;
      return;
    } else if (this.rollFees) {
      this.breakEven = 1;
    }

    // Assigning Savings for different terms
    if (generatedLifetimeSavings.length >= 180) {
      if (this.newTerm === 30 && this.currentTerm === 30) {
        this.fiveYearSavings = generatedLifetimeSavings[59].savings;
        this.tenYearSavings = generatedLifetimeSavings[119].savings;
        this.fifteenYearSavings = generatedLifetimeSavings[179].savings;
        this.twentyYearSavings = generatedLifetimeSavings[239].savings;
      }
      else if (this.newTerm === 15 && this.currentTerm === 15) {
        this.fiveYearSavings = generatedLifetimeSavings[59].savings;
        this.tenYearSavings = generatedLifetimeSavings[119].savings;
        this.fifteenYearSavings = generatedLifetimeSavings[179].savings;
      }
      else if (this.newTerm === 15) {
        this.fiveYearSavings = generatedLifetimeSavings[59].savings;
        this.tenYearSavings = generatedLifetimeSavings[119].savings;
        this.fifteenYearSavings = generatedLifetimeSavings[179].savings;
      }
    }
  }

  GenerateLifetimeSavings(newTerm: number, lifespan: number, currentLoanMonths: number, currentPayment: number) {

    lifespan = newTerm > lifespan ? newTerm : lifespan;

    let initialTerm = 2;
    let startingPoint = (this.monthlySavings * 2) + this.refinanceFees > 0 ? this.refinanceFees * -1 : 0;
    let lifetimeSavingsArray = [];
    let monthSavingsData = {
      month: 1,
      savings: startingPoint
    }

    lifetimeSavingsArray.push(monthSavingsData);

    if (newTerm < currentLoanMonths) {
      while (initialTerm < newTerm) {
        monthSavingsData = {
          month: initialTerm,
          savings: startingPoint + (this.monthlySavings * (initialTerm - 1))
        };
        lifetimeSavingsArray.push(monthSavingsData);
        initialTerm++;
      };
      while (newTerm <= lifespan) {
        let arrayLength = lifetimeSavingsArray.length;
        let postPayStartingPoint = lifetimeSavingsArray[newTerm - 2].savings;
        monthSavingsData = {
          month: arrayLength + 1,
          savings: postPayStartingPoint + currentPayment
        };
        lifetimeSavingsArray.push(monthSavingsData);
        newTerm++;
      }
    }

    if (newTerm >= currentLoanMonths) {
      while (initialTerm <= newTerm) {
        let currentSavings = lifetimeSavingsArray[initialTerm - 2].savings;
        if (initialTerm < currentLoanMonths) {
          monthSavingsData = {
            month: initialTerm,
            savings: currentSavings + this.monthlySavings
          };
          lifetimeSavingsArray.push(monthSavingsData);
          initialTerm++;
        } else if (initialTerm >= currentLoanMonths) {
          monthSavingsData = {
            month: initialTerm,
            savings: currentSavings - this.newPayment
          };
          lifetimeSavingsArray.push(monthSavingsData);
          initialTerm++;
        }
      }
    }
    return lifetimeSavingsArray;
  }
}
